import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Trash2, Workflow, Clock, MousePointerClick, GitBranch, ChevronDown, Play, Pause, Save,
} from "lucide-react";

const NODE_TEMPLATES = {
  trigger: {
    types: [
      { id: "event_purchase", label: "Order completed" },
      { id: "event_signup", label: "User signed up" },
      { id: "event_support_closed", label: "Support ticket closed" },
      { id: "event_visit", label: "Store visit (kiosk)" },
      { id: "event_api", label: "API event (webhook)" },
    ],
  },
  delay: {
    types: [
      { id: "delay_immediate", label: "Immediate" },
      { id: "delay_1h", label: "1 hour" },
      { id: "delay_24h", label: "24 hours" },
      { id: "delay_7d", label: "7 days" },
      { id: "delay_30d", label: "30 days" },
    ],
  },
  action: {
    types: [
      { id: "action_send_link", label: "Send shareable link" },
      { id: "action_send_email", label: "Send email survey" },
      { id: "action_send_sms", label: "Send SMS survey" },
      { id: "action_send_whatsapp", label: "Send WhatsApp survey" },
      { id: "action_create_ticket", label: "Create CSM ticket" },
    ],
  },
  condition: {
    types: [
      { id: "cond_detractor", label: "If response is detractor" },
      { id: "cond_promoter", label: "If response is promoter" },
      { id: "cond_segment", label: "If customer is in segment" },
      { id: "cond_no_response", label: "If no response in 7 days" },
    ],
  },
};

export default function JourneysPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState({ name: "", description: "" });
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/journeys");
    setItems(data);
    if (data.length && !active) setActive(data[0]);
    if (active) {
      const updated = data.find((j) => j.id === active.id);
      if (updated) setActive(updated);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!creating.name.trim()) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/journeys", {
        name: creating.name.trim(),
        description: creating.description.trim() || null,
        nodes: [
          { id: crypto.randomUUID(), type: "trigger", label: "Order completed", config: { event: "event_purchase" } },
          { id: crypto.randomUUID(), type: "delay", label: "24 hours", config: { delay: "delay_24h" } },
          { id: crypto.randomUUID(), type: "action", label: "Send shareable link", config: { action: "action_send_link" } },
          { id: crypto.randomUUID(), type: "condition", label: "If response is detractor", config: { cond: "cond_detractor" } },
        ],
      });
      setItems((arr) => [data, ...arr]);
      setActive(data);
      setOpen(false);
      setCreating({ name: "", description: "" });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/journeys/${active.id}`, { name: active.name, description: active.description, nodes: active.nodes });
      setActive(data);
      load();
    } finally { setBusy(false); }
  };

  const toggleActive = async () => {
    const next = active.status === "active" ? "paused" : "active";
    const { data } = await api.put(`/journeys/${active.id}`, { status: next });
    setActive(data);
    load();
  };

  const remove = async () => {
    if (!confirm("Delete this journey?")) return;
    await api.delete(`/journeys/${active.id}`);
    setActive(null);
    load();
  };

  const addNode = (type) => {
    const tpl = NODE_TEMPLATES[type].types[0];
    const node = { id: crypto.randomUUID(), type, label: tpl.label, config: { [type === "trigger" ? "event" : type === "delay" ? "delay" : type === "action" ? "action" : "cond"]: tpl.id } };
    setActive((j) => ({ ...j, nodes: [...j.nodes, node] }));
  };

  const updateNode = (id, patch) => setActive((j) => ({ ...j, nodes: j.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) }));
  const removeNode = (id) => setActive((j) => ({ ...j, nodes: j.nodes.filter((n) => n.id !== id) }));
  const moveNode = (idx, dir) => setActive((j) => {
    const arr = [...j.nodes]; const k = idx + dir;
    if (k < 0 || k >= arr.length) return j;
    [arr[idx], arr[k]] = [arr[k], arr[idx]];
    return { ...j, nodes: arr };
  });

  return (
    <div className="grid lg:grid-cols-12 gap-5 fade-in">
      <div className="lg:col-span-3 space-y-3">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="journey-new-btn" className="w-full bg-[#F58220] hover:bg-[#E36F0E] text-white"><Plus size={16} className="mr-1.5"/>New journey</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create a journey</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div><Label className="text-sm font-semibold text-[#1F3A6B]">Journey name</Label><Input data-testid="journey-name" value={creating.name} onChange={(e) => setCreating((c) => ({ ...c, name: e.target.value }))} required className="h-11 mt-1"/></div>
              <div><Label className="text-sm font-semibold text-[#1F3A6B]">Description (optional)</Label><Textarea data-testid="journey-desc" rows={3} value={creating.description} onChange={(e) => setCreating((c) => ({ ...c, description: e.target.value }))}/></div>
              {err && <p className="text-sm text-rose-600">{err}</p>}
              <DialogFooter><Button data-testid="journey-create" disabled={busy} type="submit" className="bg-[#F58220] hover:bg-[#E36F0E] text-white">Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <div className="space-y-1.5">
          {items.length === 0 && <p className="text-sm text-slate-500">No journeys yet.</p>}
          {items.map((j) => (
            <button
              key={j.id}
              data-testid={`journey-list-${j.id}`}
              onClick={() => setActive(j)}
              className={`w-full text-left px-3 py-2.5 rounded-md transition border ${
                active?.id === j.id ? "bg-[#FFF0DF] border-cyan-200 text-[#1F3A6B]" : "border-transparent hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-sm truncate">{j.name}</p>
                <Badge className={j.status === "active" ? "bg-emerald-100 text-emerald-700" : j.status === "paused" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}>{j.status}</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{j.nodes?.length || 0} steps</p>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-9 space-y-3">
        {!active ? (
          <Card className="p-12 text-center border-dashed border-2 border-slate-200">
            <Workflow size={32} className="mx-auto text-slate-300"/>
            <p className="mt-2 font-bold text-[#1F3A6B]">No journey selected</p>
            <p className="text-sm text-slate-500">Create a new journey to get started.</p>
          </Card>
        ) : (
          <>
            <Card className="p-5 border-slate-100">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <Input data-testid="journey-edit-name" value={active.name} onChange={(e) => setActive((j) => ({ ...j, name: e.target.value }))} className="text-lg font-extrabold text-[#1F3A6B] border-0 px-0 focus-visible:ring-0"/>
                  <Input value={active.description || ""} onChange={(e) => setActive((j) => ({ ...j, description: e.target.value }))} placeholder="Description" className="mt-1 text-sm border-0 px-0 focus-visible:ring-0 text-slate-600"/>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="journey-save" onClick={save} disabled={busy} variant="outline"><Save size={16} className="mr-1.5"/>Save</Button>
                  <Button data-testid="journey-toggle" onClick={toggleActive} className="bg-[#F58220] hover:bg-[#E36F0E] text-white">
                    {active.status === "active" ? <><Pause size={16} className="mr-1.5"/>Pause</> : <><Play size={16} className="mr-1.5"/>Activate</>}
                  </Button>
                  <Button data-testid="journey-delete" onClick={remove} variant="outline" className="text-rose-600 hover:text-rose-700"><Trash2 size={16}/></Button>
                </div>
              </div>
            </Card>

            <Card className="p-5 border-slate-100">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Canvas</p>
                  <p className="text-sm text-slate-600">Stack steps top-to-bottom. The journey runs in this order.</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <NodeAddBtn type="trigger" label="Trigger" icon={<MousePointerClick size={14}/>} onClick={() => addNode("trigger")}/>
                  <NodeAddBtn type="delay" label="Delay" icon={<Clock size={14}/>} onClick={() => addNode("delay")}/>
                  <NodeAddBtn type="action" label="Action" icon={<Workflow size={14}/>} onClick={() => addNode("action")}/>
                  <NodeAddBtn type="condition" label="Condition" icon={<GitBranch size={14}/>} onClick={() => addNode("condition")}/>
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-2 max-w-2xl mx-auto">
                {active.nodes.length === 0 && <p className="text-sm text-slate-500 text-center py-6">Add steps to your journey.</p>}
                {active.nodes.map((n, idx) => (
                  <React.Fragment key={n.id}>
                    <NodeCard
                      node={n}
                      onChange={(p) => updateNode(n.id, p)}
                      onUp={() => moveNode(idx, -1)}
                      onDown={() => moveNode(idx, 1)}
                      onDelete={() => removeNode(n.id)}
                    />
                    {idx < active.nodes.length - 1 && <div className="ml-6 h-4 w-px border-l-2 border-dashed border-slate-300 mx-auto"/>}
                  </React.Fragment>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function NodeAddBtn({ label, icon, onClick }) {
  return <Button size="sm" variant="outline" onClick={onClick} className="h-8"><span className="mr-1.5 text-[#F58220]">{icon}</span>{label}</Button>;
}

function NodeCard({ node, onChange, onUp, onDown, onDelete }) {
  const palette = NODE_TEMPLATES[node.type];
  const tone = {
    trigger: "bg-[#FFF0DF] border-cyan-200 text-[#1F3A6B]",
    delay: "bg-amber-50 border-amber-200 text-amber-800",
    action: "bg-emerald-50 border-emerald-200 text-emerald-800",
    condition: "bg-purple-50 border-purple-200 text-purple-800",
  }[node.type];
  const icon = {
    trigger: <MousePointerClick size={16}/>, delay: <Clock size={16}/>,
    action: <Workflow size={16}/>, condition: <GitBranch size={16}/>,
  }[node.type];
  const cfgKey = node.type === "trigger" ? "event" : node.type === "delay" ? "delay" : node.type === "action" ? "action" : "cond";
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${tone}`}>
      <div className="w-9 h-9 rounded-md bg-white grid place-items-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{node.type}</p>
        <div className="relative">
          <select
            data-testid={`node-select-${node.id}`}
            value={node.config?.[cfgKey] || palette.types[0].id}
            onChange={(e) => {
              const t = palette.types.find((tt) => tt.id === e.target.value);
              onChange({ label: t.label, config: { ...(node.config || {}), [cfgKey]: t.id } });
            }}
            className="bg-transparent appearance-none font-semibold text-sm pr-6 outline-none cursor-pointer"
          >
            {palette.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-0 top-1.5 opacity-60 pointer-events-none"/>
        </div>
      </div>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onUp}>↑</Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onDown}>↓</Button>
      <Button data-testid={`node-del-${node.id}`} size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:text-rose-700" onClick={onDelete}><Trash2 size={14}/></Button>
    </div>
  );
}
